import axios, { AxiosRequestConfig } from 'axios';
import {
  CIDResponse,
  BalanceResponse,
  RechargeResponse,
  PidKeyResponse,
  OCRResponse
} from '../types/api.types';
import { API_CONFIG } from '../config/api.config';
import { authService } from './auth';

// Criar instância do axios com configuração base
const api = axios.create({
  baseURL: API_CONFIG.BACKEND.BASE_URL,
  timeout: 60000, // Aumentado para 60 segundos
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: true, // Importante para enviar cookies e autenticação
});

// Adicionar interceptor para melhorar tratamento de timeouts
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    
    // Se encontrar o token, adiciona ao header de autorização
    if (token) {
      // Adicionar o header de autorização explicitamente para cada requisição
      config.headers = {
        ...config.headers,
        'Authorization': `Bearer ${token}`
      };
      
      // Log para debug (remove em produção)
      console.log('Token adicionado ao header:', config.url);
    } else {
      console.warn('Token não encontrado para requisição:', config.url);
    }
    
    // Para operações de consulta CID e OCR, aumentar o timeout
    if (config.url?.includes('/api/proxy/getcid') || config.url?.includes('/api/proxy/pidkey')) {
      config.timeout = 90000; // 90 segundos para operações mais demoradas
    }
    
    // Log para debug (sem expor dados sensíveis)
    // // console.log(`Enviando requisição para: ${config.url?.split('?')[0]}`);
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar erros de conexão e timeout
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (axios.isAxiosError(error)) {
      // Melhorar mensagens de erro de timeout
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        console.error('Timeout na requisição:', error.config?.url);
        // Customizar objeto de erro para melhor feedback ao usuário
        error.response = error.response || {
          status: 504,
          data: {
            message: 'A requisição demorou muito tempo para responder. Por favor, tente novamente.',
            error: 'Timeout Error'
          }
        };
      }
      
      // Tratar erros de conexão
      if (error.code === 'ECONNREFUSED' || error.code === 'ENOTFOUND') {
        console.error('Erro de conexão:', error.message);
        error.response = error.response || {
          status: 503,
          data: {
            message: 'Não foi possível conectar ao servidor. Por favor, verifique sua conexão e tente novamente.',
            error: 'Connection Error'
          }
        };
      }
      
      // Tratar erros de autenticação (401)
      const originalRequest = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;

      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        // Não tentar refresh em rotas de autenticação (login/refresh)
        !originalRequest.url?.includes('/api/auth/login') &&
        !originalRequest.url?.includes('/api/auth/refresh')
      ) {
        console.warn('401 recebido em', originalRequest.url, '- tentando renovar token antes de deslogar');
        originalRequest._retry = true;

        const token = localStorage.getItem('token');
        const refreshToken = localStorage.getItem('refreshToken');

        // Só tentar refresh se realmente havia uma sessão ativa
        if (token && refreshToken) {
          try {
            const refreshed = await authService.refreshAuthToken();
            if (refreshed) {
              const newToken = localStorage.getItem('token');
              if (newToken) {
                originalRequest.headers = {
                  ...originalRequest.headers,
                  Authorization: `Bearer ${newToken}`
                };
              }
              // Reenviar requisição original com o novo token
              return api(originalRequest);
            }
          } catch (refreshError) {
            console.error('Falha ao renovar token via interceptor de api.ts:', refreshError);
          }

          // Refresh falhou de fato: aí sim limpar e redirecionar
          console.warn('Sessão expirada e refresh falhou. Redirecionando para login.');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('refreshToken');

          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
            setTimeout(() => {
              window.location.href = '/login?session=expired';
            }, 100);
          }
        } else {
          console.warn('Requisição sem token/refreshToken. Não será feito logout automático.');
        }
      }
    }

    return Promise.reject(error);
  }
);

// Serviços de tenant (clientes)
export const getAllTenants = async () => {
  try {
    const response = await api.get('/api/tenants', {
      params: {
        include: 'parent'
      }
    });
    
    // A API retorna os dados no campo 'tenants', vamos normalizar para 'data'
    if (response.data && response.data.tenants) {
      return { 
        success: true, 
        data: response.data.tenants,
        tenants: response.data.tenants // Manter para compatibilidade com código existente
      };
    }
    
    return { 
      success: true, 
      data: response.data.tenants || [],
      tenants: response.data.tenants || [] // Manter para compatibilidade
    };
  } catch (error: any) {
    console.error('Erro ao buscar tenants:', error.response?.data || error.message);
    return { 
      success: false, 
      error: error.response?.data?.message || error.message || 'Erro ao buscar tenants',
      data: [],
      tenants: []
    };
  }
};

export const getTenantById = async (id: string) => {
  try {
    const response = await api.get(`/api/tenants/${id}`);
    // // console.log('Dados do tenant carregados com sucesso');
    return { 
      success: true, 
      tenant: response.data.tenant 
    };
  } catch (error: any) {
    console.error(`Erro ao buscar tenant ${id}`);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Erro ao buscar tenant',
      tenant: null
    };
  }
};

export const createTenant = async (tenantData: any) => {
  try {
    // // console.log('Criando novo tenant:', tenantData);
    const response = await api.post('/api/tenants', tenantData);
    // // console.log('Tenant criado:', response.data);
    return { success: true, data: response.data };
  } catch (error: any) {
    // console.error('Erro ao criar tenant:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro ao criar tenant',
      details: axios.isAxiosError(error) ? error.response?.data : null
    };
  }
};

export const updateTenant = async (tenantId: string, tenantData: any) => {
  try {
    // // console.log(`Atualizando tenant ${tenantId}:`, tenantData);
    const response = await api.put(`/api/tenants/${tenantId}`, tenantData);
    // // console.log('Tenant atualizado:', response.data);
    return { success: true, data: response.data };
  } catch (error: any) {
    console.error(`Erro ao atualizar tenant ${tenantId}:`, error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro ao atualizar tenant',
      details: axios.isAxiosError(error) ? error.response?.data : null
    };
  }
};

// Serviços de usuários
export const getAllUsers = async () => {
  try {
    // // console.log('Buscando todos os usuários...');
    const response = await api.get('/api/users');
    // // console.log('Usuários recebidos:', response.data);
    return { success: true, data: response.data.users };
  } catch (error) {
    console.error('Erro ao buscar usuários:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro ao buscar usuários'
    };
  }
};

export const getTenants = async () => {
  try {
    const response = await api.get('/api/tenants');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar tenants:', error);
    return { success: false, message: error instanceof Error ? error.message : 'Erro ao buscar tenants', users: [] };
  }
};

// Função para buscar todos os usuários
export const getUsers = async () => {
  try {
    const response = await api.get('/api/users');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar usuários:', error);
    return { success: false, message: error instanceof Error ? error.message : 'Erro ao buscar usuários', users: [] };
  }
};

// Função para buscar um usuário específico por ID
export const getUserById = async (userId: string) => {
  try {
    const response = await api.get(`/api/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar usuário:', error);
    return { success: false, message: error instanceof Error ? error.message : 'Erro ao buscar usuário' };
  }
};

export const createUser = async (userData: any) => {
  try {
    // // console.log('Criando novo usuário:', userData);
    const response = await api.post('/api/users', userData);
    // // console.log('Usuário criado:', response.data);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Erro ao criar usuário:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro ao criar usuário',
      details: axios.isAxiosError(error) ? error.response?.data : null
    };
  }
};

export const updateUser = async (userId: string, userData: any) => {
  try {
   
    const response = await api.put(`/api/users/${userId}`, userData);
    
    // // console.log('Usuário atualizado - Resposta completa:', response.data);
    return { success: true, data: response.data.data };
  } catch (error) {
    console.error(`Erro ao atualizar usuário ${userId}:`, error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro ao atualizar usuário',
      details: axios.isAxiosError(error) ? error.response?.data : null
    };
  }
};

export const toggleUserStatus = async (userId: string, active: boolean) => {
  try {
    const response = await api.patch(`/api/users/${userId}/status`, { active });
    return { success: true, data: response.data };
  } catch (error) {
    console.error(`Erro ao alterar status do usuário ${userId}:`, error);
    
    // Log detalhado do erro para depuração
    if (axios.isAxiosError(error)) {
      console.error('Detalhes do erro da API:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
        config: error.config
      });
    }
    
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro ao alterar status do usuário'
    };
  }
};

export const getCID = async (iid: string): Promise<CIDResponse> => {
  try {
    // // console.log('Consultando CID');
    const response = await api.get(`/api/proxy/getcid/getcid?iid=${iid}`);
    // // console.log('Consulta de CID concluída');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Erro ao consultar CID');
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro ao consultar CID'
    };
  }
};

export const getBalance = async (): Promise<BalanceResponse> => {
  try {
    console.log('Consultando saldo - Iniciando requisição');
    // Verificar token no localStorage
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('Consultando saldo - Token não encontrado no localStorage');
      return { 
        success: false, 
        error: 'Usuário não autenticado. Faça login novamente.'
      };
    }
    console.log('Consultando saldo - Token encontrado, iniciando requisição');
    
    const response = await api.get('/api/proxy/getcid/balance');
    console.log('Consulta de saldo concluída com sucesso');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Erro ao consultar saldo:', error);
    if (axios.isAxiosError(error)) {
      console.error('Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
    }
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro ao consultar saldo'
    };
  }
};

export const recharge = async (orderId: string): Promise<RechargeResponse> => {
  try {
    // // console.log('Iniciando recarga de créditos');
    const response = await api.get(`/api/proxy/getcid/recharge?orderid=${orderId}`);
    // // console.log('Recarga de créditos concluída');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Erro ao recarregar créditos');
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro ao recarregar'
    };
  }
};

export const checkPidKey = async (pidKey: string): Promise<PidKeyResponse> => {
  try {
    console.log('Verificando PidKey - Iniciando requisição para:', pidKey);
    
    // Verificar token no localStorage
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('Verificando PidKey - Token não encontrado no localStorage');
      return { 
        success: false, 
        error: 'Usuário não autenticado. Faça login novamente.'
      };
    }
    
    console.log('Verificando PidKey - Token encontrado, iniciando requisição com timeout estendido');
    
    const response = await api.get(`/api/proxy/pidkey/checkkey?pidkey=${pidKey}`, {
      timeout: 120000
    });
    
    console.log('Verificação de PidKey concluída com sucesso');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Erro ao verificar PidKey:', error);
    if (axios.isAxiosError(error)) {
      console.error('Detalhes do erro na verificação de PidKey:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
    }
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro ao verificar PidKey'
    };
  }
};

export const performOCR = async (base64Image: string): Promise<OCRResponse> => {
  try {
    // // console.log('Iniciando OCR');
    const response = await api.post('/api/proxy/pidkey/ocr', { base64: base64Image }, {
      timeout: 180000
    });
    // // console.log('OCR concluído');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Erro ao realizar OCR');
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro ao realizar OCR'
    };
  }
};

// Atualizar senha do tenant
export const updateTenantPassword = async (tenantId: string, data: { password: string, confirmPassword: string }) => {
  try {
    const response = await api.post(`/api/tenants/${tenantId}/password`, data);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Erro ao atualizar senha do tenant:', error);
    if (axios.isAxiosError(error) && error.response) {
      return { 
        success: false, 
        error: error.response.data.message || 'Erro ao atualizar senha do tenant',
        data: error.response.data
      };
    }
    return { success: false, error: 'Erro de conexão' };
  }
};

// Adicionar créditos a um tenant
export const addCredits = async (tenantId: string, data: { amount: number, description?: string }) => {
  try {
    // // console.log(`Adicionando ${data.amount} créditos ao tenant ${tenantId}`);
    const response = await api.post(`/api/tenants/${tenantId}/credits`, data);
    // // console.log('Resposta da adição de créditos:', response.data);
    return { success: true, data: response.data };
  } catch (error: any) {
    console.error(`Erro ao adicionar créditos ao tenant ${tenantId}:`, error);
    if (axios.isAxiosError(error) && error.response) {
      return { 
        success: false, 
        error: error.response.data.message || 'Erro ao adicionar créditos',
        data: error.response.data
      };
    }
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro ao adicionar créditos'
    };
  }
};

// Buscar histórico de transações de um tenant (recargas e requisições)
export const getTenantTransactions = async (tenantId: string) => {
  try {
    // // console.log('Buscando histórico de transações');
    
    // Buscar histórico de recargas
    const rechargesResponse = await api.get(`/api/tenants/${tenantId}/credits/history`);
    // // console.log('Histórico de recargas carregado');
    
    // Buscar histórico de requisições (débitos)
    const requestsResponse = await api.get(`/api/queries/tenants/${tenantId}/requests`);
    // // console.log('Histórico de requisições carregado');
    
    return { 
      success: true, 
      data: {
        recharges: rechargesResponse.data.recharges || [],
        requests: requestsResponse.data.requests || []
      }
    };
  } catch (error: any) {
    console.error('Erro ao buscar histórico de transações');
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro ao buscar histórico de transações',
      data: { recharges: [], requests: [] }
    };
  }
};

// Atualizar preço de consulta para um tenant
export const updateTenantPrice = async (tenantId: string, queryTypeId: string, price: number) => {
  try {
    // console.log(`Atualizando preço da consulta ${queryTypeId} para o tenant ${tenantId}:`, price);
    const response = await api.put(`/api/tenants/${tenantId}/prices/${queryTypeId}`, { price });
    // console.log('Preço atualizado:', response.data);
    return { success: true, data: response.data };
  } catch (error: any) {
    console.error(`Erro ao atualizar preço para o tenant ${tenantId}:`, error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro ao atualizar preço',
      details: axios.isAxiosError(error) ? error.response?.data : null
    };
  }
};

// Interface para os filtros de requisições
export interface RequestFilters {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  queryType?: string;
  user?: string;
  userId?: string; // Adicionar userId para compatibilidade
}

/**
 * Obtém o histórico de requisições do usuário atual
 * @param options Filtros e opções de paginação
 */
export const getUserRequestHistory = async (options: RequestFilters = {}) => {
  // console.log('Chamando getUserRequestHistory com opções:', options);
  
  try {
    const response = await api.get('/api/queries/my/requests', {
      params: {
        page: options.page || 1,
        limit: options.limit || 10,
        status: options.status,
        startDate: options.startDate,
        endDate: options.endDate,
        queryType: options.queryType
      }
    });
    
    // console.log('Resposta do getUserRequestHistory:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Erro ao buscar histórico de requisições do usuário:', error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Erro ao buscar histórico de requisições',
      requests: [],
      total: 0
    };
  }
};

/**
 * Obtém o histórico de requisições de um tenant específico
 * @param tenantId ID do tenant
 * @param options Filtros e opções de paginação
 */
export const getTenantRequestHistory = async (tenantId: string, options: RequestFilters = {}) => {
  // console.log('Chamando getTenantRequestHistory com opções:', { tenantId, ...options });
  
  try {
    const response = await api.get(`/api/queries/tenants/${tenantId}/requests`, {
      params: {
        page: options.page || 1,
        limit: options.limit || 10,
        startDate: options.startDate,
        endDate: options.endDate,
        userId: options.userId || options.user,
        queryType: options.queryType
      }
    });
    
    // console.log('Resposta do getTenantRequestHistory:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Erro ao buscar histórico de requisições do tenant:', error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Erro ao buscar histórico de requisições',
      requests: [],
      total: 0
    };
  }
};

// Função para buscar os sub-tenants (clientes) vinculados a um tenant específico
export const getSubTenants = async (parentId: string) => {
  try {
    // // console.log(`Buscando sub-tenants para o tenant ${parentId}`);
    const response = await api.get(`/api/tenants/${parentId}/subtenants`);
    // // console.log('Resposta da API:', response.data);
    return { 
      success: true, 
      subTenants: response.data.subTenants
    };
  } catch (error) {
    console.error(`Erro ao buscar sub-tenants para o tenant ${parentId}:`, error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro ao buscar clientes vinculados',
      subTenants: []
    };
  }
};

// Buscar todos os tipos de consulta disponíveis
export const getAllQueryTypes = async () => {
  try {
    // console.log('Buscando todos os tipos de consulta disponíveis');
    const response = await api.get('/api/queries/types');
    // console.log('Tipos de consulta recebidos:', response.data);
    return { 
      success: true, 
      data: response.data.queryTypes 
    };
  } catch (error: any) {
    console.error('Erro ao buscar tipos de consulta:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro ao buscar tipos de consulta',
      data: []
    };
  }
};

// Configurar preços padrão para um tenant
export const setupDefaultPrices = async (tenantId: string) => {
  try {
    // console.log(`Configurando preços padrão para o tenant ${tenantId}`);
    const response = await api.post(`/api/tenants/${tenantId}/setup-default-prices`);
    // console.log('Preços padrão configurados:', response.data);
    return { 
      success: true, 
      data: response.data 
    };
  } catch (error: any) {
    console.error(`Erro ao configurar preços padrão para o tenant ${tenantId}:`, error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro ao configurar preços padrão',
      details: axios.isAxiosError(error) ? error.response?.data : null
    };
  }
};

// Buscar tipos de consulta (alias para getAllQueryTypes para compatibilidade)
export const getQueryTypes = getAllQueryTypes;

export const resetUserPassword = async (userId: string) => {
  try {
    // // console.log(`Resetando senha do usuário ${userId} para padrão`);
    
    const response = await api.post(`/api/users/${userId}/reset-password`);
    
    // // console.log('Senha resetada com sucesso:', response.data);
    return { success: true, data: response.data };
  } catch (error) {
    console.error(`Erro ao resetar senha do usuário ${userId}:`, error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro ao resetar senha',
      details: axios.isAxiosError(error) ? error.response?.data : null
    };
  }
};

export const deleteUser = async (userId: string) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('Token de autenticação não encontrado');
      return { 
        success: false, 
        error: 'Usuário não autenticado',
        data: []
      };
    }

    // console.log('Enviando requisição para excluir usuário:', userId);
    const response = await api.delete(`/api/users/${userId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    // console.log('Resposta da exclusão de usuário:', response.data);
    return { success: true, data: response.data };
  } catch (error: any) {
    console.error('Erro ao excluir usuário:', error);
    console.error('Detalhes do erro:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      data: error.response?.data
    });
    return { 
      success: false, 
      error: error.response?.data?.message || (error instanceof Error ? error.message : 'Erro ao excluir usuário'),
      data: []
    };
  }
};

// Função para buscar usuários de um tenant específico
export const getUsersByTenant = async (tenantId: string) => {
  try {
    // // console.log(`Buscando usuários do tenant: ${tenantId}`);
    const response = await api.get(`/api/users/tenant/${tenantId}`);
    // // console.log('Resposta da API de usuários do tenant:', response.status);
    return { 
      success: true, 
      data: response.data.data 
    };
  } catch (error) {
    console.error(`Erro ao buscar usuários do tenant ${tenantId}:`, error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro ao buscar usuários do tenant',
      data: []
    };
  }
};

/**
 * Obtém o histórico de todas as requisições (apenas para administradores)
 * @param options Filtros e opções de paginação
 */
export const getAllRequestsHistory = async (options: RequestFilters = {}) => {
  // console.log('Chamando getAllRequestsHistory com opções:', options);
  
  try {
    const response = await api.get('/api/queries/admin/all-requests', {
      params: {
        page: options.page || 1,
        limit: options.limit || 10,
        startDate: options.startDate,
        endDate: options.endDate,
        userId: options.userId,
        tenantId: options.tenantId,
        queryType: options.queryType
      }
    });
    
    // console.log('Resposta do getAllRequestsHistory:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Erro ao buscar histórico completo de requisições:', error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Erro ao buscar histórico de requisições',
      requests: [],
      total: 0
    };
  }
};

/**
 * Obtém estatísticas gerais do sistema para relatórios
 * @param startDate Data inicial no formato YYYY-MM-DD
 * @param endDate Data final no formato YYYY-MM-DD
 */
export const getSystemStats = async (startDate?: string, endDate?: string) => {
  try {
    // console.log('Buscando estatísticas do sistema:', { startDate, endDate });
    const response = await api.get('/api/queries/stats', {
      params: {
        start: startDate,
        end: endDate
      }
    });
    // console.log('Resposta das estatísticas do sistema:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Erro ao buscar estatísticas do sistema:', error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Erro ao buscar estatísticas',
      data: null
    };
  }
};

/**
 * Obtém os top clientes por consumo
 * @param startDate Data inicial no formato YYYY-MM-DD
 * @param endDate Data final no formato YYYY-MM-DD
 */
export const getTopClients = async (startDate?: string, endDate?: string) => {
  try {
    // console.log('Buscando top clientes:', { startDate, endDate });
    const response = await api.get('/api/queries/top-clients', {
      params: {
        start: startDate,
        end: endDate
      }
    });
    // console.log('Resposta dos top clientes:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Erro ao buscar top clientes:', error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Erro ao buscar top clientes',
      topClients: []
    };
  }
};

/**
 * Obtém uso mensal do sistema
 * @param startDate Data inicial no formato YYYY-MM-DD
 * @param endDate Data final no formato YYYY-MM-DD
 */
export const getMonthlyUsage = async (startDate?: string, endDate?: string) => {
  try {
    // console.log('Buscando uso mensal:', { startDate, endDate });
    const response = await api.get('/api/queries/monthly-usage', {
      params: {
        start: startDate,
        end: endDate
      }
    });
    // console.log('Resposta do uso mensal:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Erro ao buscar uso mensal:', error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Erro ao buscar uso mensal',
      monthlyUsage: []
    };
  }
};

// =============================================================
// 2FA — Verificação em duas etapas (TOTP)
// =============================================================

export interface TwoFactorStatus {
  enabled: boolean;
  pending: boolean;
}

export interface TwoFactorSetupResponse {
  secret: string;
  otpauthUrl: string;
  qrCodeDataUrl: string;
}

export const get2FAStatus = async (): Promise<{ success: boolean; status?: TwoFactorStatus; error?: string }> => {
  try {
    const response = await api.get('/api/auth/2fa/status');
    return {
      success: true,
      status: {
        enabled: Boolean(response.data?.enabled),
        pending: Boolean(response.data?.pending)
      }
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Erro ao consultar status do 2FA'
    };
  }
};

export const setup2FA = async (): Promise<{ success: boolean; data?: TwoFactorSetupResponse; error?: string }> => {
  try {
    const response = await api.post('/api/auth/2fa/setup');
    return {
      success: true,
      data: {
        secret: response.data?.secret,
        otpauthUrl: response.data?.otpauthUrl,
        qrCodeDataUrl: response.data?.qrCodeDataUrl
      }
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Erro ao iniciar configuração do 2FA'
    };
  }
};

export const verify2FA = async (code: string): Promise<{ success: boolean; error?: string }> => {
  try {
    await api.post('/api/auth/2fa/verify', { code });
    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Código inválido'
    };
  }
};

export const disable2FA = async (
  password: string,
  code?: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    await api.post('/api/auth/2fa/disable', { password, code });
    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Não foi possível desativar o 2FA'
    };
  }
};

export default api; 